import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Landmark, CreditCard, Send, CheckCircle2, Clock, Loader2, Smartphone, Globe } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

const StatusBadge = ({ status, exempt }) => {
  const { t } = useTranslation();
  if (exempt) {
    return <span className="kv-label" style={{ color: "#5a5a5a", border: "1px solid #9a9a9a", padding: "2px 8px", fontSize: "0.62rem" }} data-testid="contrib-status-exempt">{t("contrib.statusExempt", "EXONERAT")}</span>;
  }
  const map = {
    pending: { txt: t("contrib.statusPending", "ÎN AȘTEPTARE"), color: "#8a6f1c", border: "#c9a84c", bg: "#fdf4e0" },
    claimed: { txt: t("contrib.statusClaimed", "ÎN VERIFICARE"), color: "#1d4e89", border: "#1d4e89", bg: "#e8f0fb" },
    paid: { txt: t("contrib.statusPaid", "ACHITAT"), color: "#3e6a3c", border: "#3e6a3c", bg: "#e9f3e8" },
  };
  const s = map[status] || map.pending;
  return (
    <span className="kv-label" style={{ color: s.color, border: `1px solid ${s.border}`, background: s.bg, padding: "2px 8px", fontSize: "0.62rem" }} data-testid={`contrib-status-${status}`}>
      {s.txt}
    </span>
  );
};

const Breakdown = ({ c, prices }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "0.9rem", color: "#34322d" }}>
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-2"><Globe size={14} color="#9a7530" /> {t("contrib.webFee", "Aplicația web")}</span>
        <strong>{eur(c.lodge_fee)}</strong>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-2">
          <Smartphone size={14} color="#9a7530" />
          {t("contrib.mobileFee", { count: c.mobile_members, price: Number(prices?.member_month || 3).toFixed(2), defaultValue: `Membri mobili: ${c.mobile_members} × ${Number(prices?.member_month || 3).toFixed(2)} €` })}
        </span>
        <strong>{eur(c.member_fee)}</strong>
      </div>
      <div className="gold-divider" style={{ margin: "4px 0" }} />
      <div className="flex items-center justify-between gap-4">
        <span className="kv-label">{t("contrib.total", "TOTAL DE TRIMIS")}</span>
        <strong style={{ fontFamily: "Cinzel, serif", fontSize: "1.35rem", color: "#0d1b2a" }} data-testid="contrib-total">{eur(c.total)}</strong>
      </div>
    </div>
  );
};

/** Child lodge admin view — how much to send to the obedience this month. */
export const ObedienceContributionTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/contributions/my");
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) {
    return <div style={{ textAlign: "center", padding: "2rem", color: "#9a7530" }}><Loader2 className="animate-spin inline" size={20} /></div>;
  }
  const c = data.current;

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/contributions/${c.id}/claim-transfer`);
      toast.success(t("contrib.claimedOk", "Am notificat obediența — plata este în verificare."));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const payCard = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/contributions/${c.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="contrib-tab">
      <div className="gold-card" style={{ padding: "1.4rem", maxWidth: 640 }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="kv-label" style={{ fontSize: "0.66rem" }}>{t("contrib.currentMonth", "LUNA CURENTĂ")} · {c.period}</div>
            <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "1.1rem", color: "#0d1b2a", margin: "0.2rem 0 0" }}>
              {t("contrib.title", "Contribuția către Obediență")}{data.obedience_name ? ` — ${data.obedience_name}` : ""}
            </h3>
          </div>
          <StatusBadge status={c.status} exempt={c.exempt} />
        </div>
        <Breakdown c={c} prices={data.prices} />
        {!c.exempt && c.status !== "paid" && (
          <div className="flex gap-2 flex-wrap mt-5">
            {data.card_enabled && (
              <button type="button" className="btn-primary-gold flex items-center gap-2" onClick={payCard} disabled={busy} data-testid="contrib-pay-card-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} {t("contrib.payCard", "PLĂTEȘTE CU CARDUL")}
              </button>
            )}
            {c.status === "pending" && (
              <button type="button" className="btn-outline-gold flex items-center gap-2" onClick={claim} disabled={busy} data-testid="contrib-claim-btn">
                <Send size={14} /> {t("contrib.claimBtn", "AM TRIMIS PRIN TRANSFER")}
              </button>
            )}
          </div>
        )}
        {c.status === "paid" && (
          <div className="flex items-center gap-2 mt-4" style={{ color: "#3e6a3c", fontSize: "0.85rem" }}>
            <CheckCircle2 size={15} /> {t("contrib.paidAt", "Achitat la")} {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}{c.method ? ` · ${c.method}` : ""}
          </div>
        )}
        {data.bank && !c.exempt && c.status !== "paid" && (
          <div className="mt-5" style={{ padding: "0.9rem 1rem", background: "rgba(154,117,48,0.08)", border: "1px solid rgba(154,117,48,0.35)", borderRadius: 8 }} data-testid="contrib-bank-box">
            <div className="kv-label" style={{ fontSize: "0.66rem", marginBottom: 6 }}>{t("contrib.bankTitle", "TRANSFER BANCAR CĂTRE OBEDIENȚĂ")}</div>
            <div style={{ fontSize: "0.85rem", color: "#34322d", display: "flex", flexDirection: "column", gap: 3 }}>
              {data.bank.beneficiary && <div><strong>{t("contrib.beneficiary", "Titular cont")}:</strong> {data.bank.beneficiary}</div>}
              <div><strong>IBAN:</strong> {data.bank.iban}</div>
              {data.bank.bank_name && <div><strong>{t("contrib.bank", "Banca")}:</strong> {data.bank.bank_name}</div>}
              <div><strong>{t("contrib.reference", "Referință")}:</strong> {t("contrib.refValue", { period: c.period, defaultValue: `Contribuție ${c.period}` })}</div>
            </div>
          </div>
        )}
      </div>

      <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", letterSpacing: "0.16em", color: "#9a7530", margin: "1.6rem 0 0.6rem" }}>
        {t("contrib.history", "ISTORIC")}
      </h3>
      {data.history.length === 0 ? (
        <p style={{ color: "#6b6a65", fontSize: "0.85rem" }}>{t("contrib.noHistory", "Nu există luni anterioare înregistrate.")}</p>
      ) : (
        <div className="table-wrap">
          <table className="lodge-table" data-testid="contrib-history-table">
            <thead>
              <tr>
                <th>{t("contrib.period", "Perioadă")}</th>
                <th><Smartphone size={12} style={{ display: "inline" }} /> {t("contrib.mobileCol", "Membri mobili")}</th>
                <th>{t("contrib.total", "Total")}</th>
                <th>{t("contrib.status", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((h) => (
                <tr key={h.id}>
                  <td>{h.period}</td>
                  <td>{h.mobile_members}</td>
                  <td>{eur(h.total)}</td>
                  <td><StatusBadge status={h.status} exempt={h.exempt} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Obedience admin mirror — who paid / who didn't, with receipt confirmation. */
export const ContributionsOverviewTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/contributions/overview");
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) {
    return <div style={{ textAlign: "center", padding: "2rem", color: "#9a7530" }}><Loader2 className="animate-spin inline" size={20} /></div>;
  }

  const confirm = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/contributions/${id}/mark-paid`);
      toast.success(t("contrib.confirmedOk", "Primirea a fost confirmată."));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(null); }
  };

  const Row = ({ r, showLodge }) => (
    <tr data-testid={`contrib-row-${r.id}`}>
      {showLodge && <td style={{ fontWeight: 600 }}>{r.lodge_name || "—"}</td>}
      <td>{r.period}</td>
      <td>{r.mobile_members}</td>
      <td>{eur(r.total)}</td>
      <td><StatusBadge status={r.status} exempt={r.exempt} /></td>
      <td>
        {!r.exempt && r.status !== "paid" ? (
          <button type="button" className="btn-outline-gold flex items-center gap-2" style={{ fontSize: "0.68rem", padding: "4px 10px" }}
            onClick={() => confirm(r.id)} disabled={busyId === r.id} data-testid={`contrib-confirm-${r.id}`}>
            {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {t("contrib.confirmBtn", "CONFIRMĂ PRIMIREA")}
          </button>
        ) : r.status === "paid" ? (
          <span style={{ color: "#3e6a3c", fontSize: "0.75rem" }}>
            {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}{r.method ? ` · ${r.method}` : ""}
          </span>
        ) : null}
      </td>
    </tr>
  );

  return (
    <div data-testid="contrib-overview-tab">
      <div className="flex items-center gap-2 mb-3">
        <Landmark size={18} color="#9a7530" />
        <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "1.05rem", color: "#0d1b2a", margin: 0 }}>
          {t("contrib.overviewTitle", "Contribuțiile lojilor copil")} · {data.period}
        </h3>
      </div>
      {data.lodges.length === 0 ? (
        <p style={{ color: "#6b6a65", fontSize: "0.9rem" }}>{t("contrib.noLodges", "Nu există loji copil în jurisdicție.")}</p>
      ) : (
        <div className="table-wrap">
          <table className="lodge-table" data-testid="contrib-overview-table">
            <thead>
              <tr>
                <th>{t("contrib.lodge", "Lojă")}</th>
                <th>{t("contrib.period", "Perioadă")}</th>
                <th>{t("contrib.mobileCol", "Membri mobili")}</th>
                <th>{t("contrib.total", "Total")}</th>
                <th>{t("contrib.status", "Status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.lodges.map((r) => <Row key={r.id} r={r} showLodge />)}
            </tbody>
          </table>
        </div>
      )}
      {data.history.length > 0 && (
        <>
          <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", letterSpacing: "0.16em", color: "#9a7530", margin: "1.6rem 0 0.6rem" }}>
            {t("contrib.history", "ISTORIC")}
          </h3>
          <div className="table-wrap">
            <table className="lodge-table">
              <thead>
                <tr>
                  <th>{t("contrib.lodge", "Lojă")}</th>
                  <th>{t("contrib.period", "Perioadă")}</th>
                  <th>{t("contrib.mobileCol", "Membri mobili")}</th>
                  <th>{t("contrib.total", "Total")}</th>
                  <th>{t("contrib.status", "Status")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((r) => <Row key={r.id} r={r} showLodge />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
